import {
    Dispatch,
    SetStateAction,
    useEffect
} from 'react'
import Modal from "../../../shared/components/UI/Modal/Modal";
import Input from "../../../shared/components/UI/Input"
import { Controller, useForm } from "react-hook-form";
import Checkbox from "../../../shared/components/UI/Checkbox";
import Button from '../../../shared/components/UI/Button';
import LabeledInput from '../../../shared/components/UniversalEditModal/components/LabeledInput';
import LabeledTextarea from '../../../shared/components/UniversalEditModal/components/LabeledTextarea';

import "../styles/modal/AddSideModal.scss"

const defaultInitialData = {
    name: '',
    description: '',
    is_registration_allowed: false
}

interface AddSideModalProps {
    isOpen: boolean,
    setIsOpen: Dispatch<SetStateAction<boolean>>,
    isCreate: boolean,
    initialData?: SideFormData,
    onSubmit: (side: SideFormData) => void,
    onDelete?: (side: SideFormData) => void
}

interface SideFormData {
    id?: string,
    name: string,
    description: string,
    is_registration_allowed: boolean,
    size_limit: number,
}

const AddSideModal = ({
    isOpen,
    setIsOpen,
    isCreate,
    initialData,
    onSubmit,
    onDelete
}: AddSideModalProps) => {

    const {
        register,
        handleSubmit,
        formState,
        control,
        watch,
        reset
    } = useForm<SideFormData>({
        defaultValues: defaultInitialData,
        mode: 'all'
    })

    useEffect(() => {
        if (initialData) {
            reset(initialData)
        } else {
            reset(defaultInitialData)
        }
    }, [initialData, reset, isOpen])

    const closeModal = () => {
        setIsOpen(false)
    }

    const onSubmitInternal = (data: SideFormData) => {
        onSubmit(data)
        setIsOpen(false)
    }

    const errors = formState.errors

    return (
        <Modal closeModal={closeModal} isOpen={isOpen} className='add-side_modal'>
            <form>
                <div className="add-side">
                    <div className="add-side_header">
                        <h1>
                            Сторона
                        </h1>
                        <h2>
                            Форма редактирования стороны
                        </h2>
                    </div>

                    <div className="add-side_info">
                        <div className='add-side_info__input-with-description'>
                            <LabeledInput
                                label="Название"
                                inputName="name"
                                watch={watch}
                                errors={errors}
                                classes={{
                                    wrapper: 'manageEventModal__createForm-input-wrapper',
                                    field: 'manageEventModal__createForm-input-field'
                                }}
                                {...register("name", { minLength: 3, maxLength: 15, required: true })}
                            />
                            {formState.errors.name?.type === 'required' && (
                                <p className='invalid-text'>
                                    Это обязательное поле
                                </p>
                            )}
                            {formState.errors.name?.type === 'minLength' && (
                                <p className='invalid-text'>
                                    Название должно содержать не менее 3 символов
                                </p>
                            )}
                            {formState.errors.name?.type === 'maxLength' && (
                                <p className='invalid-text'>
                                    Название должно содержать не более 15 символов
                                </p>
                            )}
                        </div>

                        <div className='add-side_info__input-with-description'>
                            <LabeledTextarea
                                label="Описание стороны"
                                textareaName="description"
                                errors={errors}
                                watch={watch}
                                classes={{
                                    field: 'manageEventModal__createForm-textarea-field'
                                }}
                                {...register("description", { minLength: 5, maxLength: 500, required: true  })}
                            />
                            {formState.errors.description?.type === 'required' && (
                                <p className='invalid-text'>
                                    Это обязательное поле
                                </p>
                            )}
                            {formState.errors.description?.type === 'minLength' && (
                                <p className='invalid-text'>
                                    Описание должно содержать не менее 5 символов
                                </p>
                            )}
                            {formState.errors.description?.type === 'maxLength' && (
                                <p className='invalid-text'>
                                    Описание может содержать не более 500 символов
                                </p>
                            )}
                        </div>

                        <div className="create-event__manual-confirmation">
                            <Controller
                                name="is_registration_allowed"
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                    <Checkbox
                                        label={'Регистрация открыта'}
                                        name="manualConfirm"
                                        value="manualConfirm"
                                        labelStyle={{}}
                                        checked={field.value}
                                        onChange={(checked: any) => field.onChange(checked)}
                                        className="darkCheckbox"
                                        textColor='#62666D'
                                    />
                                )}
                            />
                        </div>

                        <div>
                            <div className="floating-label-input">
                                <LabeledInput
                                    label="Лимит регистрации игроков"
                                    inputName="size_limit"
                                    watch={watch}
                                    errors={errors}
                                    classes={{
                                        wrapper: 'manageEventModal__createForm-input-wrapper',
                                        field: 'manageEventModal__createForm-input-field'
                                    }}
                                    {...register("size_limit", {
                                        minLength: 1,
                                        maxLength: 4,
                                        pattern: {
                                            value: /^[0-9]+$/,
                                            message: 'Можно вводить только цифры'
                                        },
                                    })}
                                />
                            </div>

                            {!formState.errors.size_limit && (<p className="create-event__description">
                                Вы можете добавить лимит регистрации игроков <br />
                                Значение "0" - убирает лимит регистрации на сторону
                            </p>)}

                            {formState.errors.size_limit?.type === 'pattern' && (
                                <p className="create-event__description invalid-text">
                                    Можно вводить только цифры
                                </p>
                            )}

                            {formState.errors.size_limit?.type === 'maxLength' && (
                                <p className="create-event__description invalid-text">
                                    Слишком большой лимит регистрации на сторону
                                </p>
                            )}
                        </div>

                        <div className="add-side_buttons">

                            {!isCreate && <div
                                className={`delete-side ${!isCreate ? 'visible' : ''}`}
                                onClick={handleSubmit((data) => {
                                    onDelete?.(data)
                                    setIsOpen(false)
                                })}
                            >
                                <p>Удалить сторону</p>
                            </div>}

                            <Button
                                className="add-side_button"
                                type="button"
                                onClick={handleSubmit((data) => {
                                    onSubmit({ ...data, size_limit: Number(data.size_limit) })
                                    setIsOpen(false)
                                })}
                            >
                                {isCreate ? "Создать сторону" : "Сохранить"}
                            </Button>

                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    )
}

export default AddSideModal